import { useMemo } from 'react'
import { useAuthContext } from '@/lib/auth-context'
import {
  Pager,
  Section,
  TableScroller,
  useBearer,
  usePagedQuery,
  useToast,
} from './_admin-utils'

interface AuditLogItem {
  id?: string
  created_at?: string
  actor_id?: string
  actor_email?: string
  actor?: string
  action?: string
  event?: string
  type?: string
  target_email?: string
  target?: string
  entity?: string
  subject?: string
  metadata?: unknown
  meta?: unknown
  details?: unknown
}

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

const stringifyMeta = (meta: unknown): string => {
  if (!meta) return '—'
  if (typeof meta === 'string') return meta
  try {
    return JSON.stringify(meta)
  } catch {
    return String(meta)
  }
}

export default function AuditLog() {
  const { profile } = useAuthContext()
  const bearer = useBearer()
  const { ToastHost } = useToast()

  const fetcher = useMemo(
    () =>
      async ({ page, pageSize }: { page: number; pageSize: number }) => {
        if (!bearer) throw new Error('Not authenticated')
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        })
        const response = await fetch(`/api/v1/admin/audit-log?${params.toString()}`, {
          headers: { ...bearer },
        })
        const json = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(json?.error?.message || json?.error || 'Failed to load audit log')
        }
        const d = json?.data || json
        return {
          items: Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : [],
          total: typeof d?.total === 'number' ? d.total : 0,
        }
      },
    [bearer],
  )

  const { items, page, pageSize, pageCount, loading, error, load } = usePagedQuery<AuditLogItem>(fetcher, [fetcher])
  const isAdmin = ['admin', 'superadmin'].includes(profile?.role ?? '')

  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>
  }

  const handlePage = (next: number) => {
    if (loading || next === page) return
    load(next)
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Audit Log</h1>
        <p className="text-sm text-cocoa/70 max-w-2xl">
          Track privileged actions executed through the admin surface. Entries are read-only and surfaced directly from the
          audit log table.
        </p>
      </header>

      <Section
        title="Activity"
        actions={<a href="/portal/admin" className="text-sm underline text-cocoa/70 hover:text-cocoa">← Admin</a>}
      >
        <div className="overflow-hidden rounded-xl border border-white/70">
          <TableScroller minWidth={820}>
            <table className="min-w-full divide-y divide-white/60 text-sm">
              <thead className="bg-shell/60 text-cocoa/70 uppercase tracking-wide text-xs">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">When</th>
                  <th className="px-4 py-3 font-semibold">Actor</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                  <th className="px-4 py-3 font-semibold">Target</th>
                  <th className="px-4 py-3 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/60 bg-white/60">
                {loading ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-cocoa/60" colSpan={5}>
                      Loading activity…
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-red-600" colSpan={5}>
                      {error}
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-cocoa/60" colSpan={5}>
                      No audit entries yet.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id || `${item.created_at}-${item.actor_id || 'actor'}`} className="hover:bg-shell/40">
                      <td className="px-4 py-3 text-cocoa/80">{formatDateTime(item.created_at)}</td>
                      <td className="px-4 py-3 text-cocoa/80">{item.actor_email || item.actor || item.actor_id || '—'}</td>
                      <td className="px-4 py-3 text-cocoa/90 font-medium">
                        {item.action || item.event || item.type || '—'}
                      </td>
                      <td className="px-4 py-3 text-cocoa/80">
                        {item.target_email || item.target || item.entity || item.subject || '—'}
                      </td>
                      <td className="px-4 py-3 text-cocoa/80">
                        <span className="block max-w-xs truncate" title={stringifyMeta(item.metadata || item.meta || item.details)}>
                          {stringifyMeta(item.metadata || item.meta || item.details)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableScroller>
        </div>
        <div className="pt-4">
          <Pager page={page} pageCount={pageCount} onPage={handlePage} />
        </div>
      </Section>

      <ToastHost />
    </div>
  )
}
